import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Gift, ArrowRight } from 'lucide-react';
import IndicatorCard from '../components/IndicatorCard';
import { fetchProducts } from '../features/products/productsSlice';
import { translateProduct } from '../utils/productTranslations';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { Button, SectionHeading } from '../components/ui';
import Seo from '../components/Seo';
import { getStaticMeta } from '../seo/pageMeta';

const FreeScreen = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { products, loading, error, status } = useSelector((state) => state.products);
  const meta = getStaticMeta('free', i18n.language);
  const catalogReady = status === 'succeeded' || status === 'failed';

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.ninjatrader.com/partner/widgetScript.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const freeProducts = Array.isArray(products)
    ? products.filter((product) => product.price === 0)
    : [];

  if (loading) {
    return (
      <>
        <Seo title={meta.title} description={meta.description} path="/free" ready={false} />
        <Loader />
      </>
    );
  }
  if (error) {
    return (
      <>
        <Seo title={meta.title} description={meta.description} path="/free" ready={catalogReady} />
        <Message variant="danger">{error}</Message>
      </>
    );
  }

  const title = i18n.language === 'fr' ? 'Indicateurs gratuits' : 'Free indicators';
  const subtitle =
    i18n.language === 'fr'
      ? 'Commencez avec des outils professionnels, sans engagement.'
      : 'Start with professional tools, with no commitment.';

  return (
    <div className="min-h-screen bg-bg pt-28 pb-24 md:pt-32">
      <Seo title={meta.title} description={meta.description} path="/free" ready={catalogReady} />
      <div className="mx-auto max-w-page px-6">
        <SectionHeading
          index={i18n.language === 'fr' ? 'Gratuit' : 'Free'}
          title={title}
          subtitle={subtitle}
        />

        <div className="mb-10 inline-flex items-center gap-2 rounded-btn border border-white/[.08] bg-surface px-4 py-2 text-sm text-muted">
          <Gift className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>
            {freeProducts.length}{' '}
            {i18n.language === 'fr'
              ? 'indicateurs gratuits disponibles'
              : 'free indicators available'}
          </span>
        </div>

        {freeProducts.length === 0 ? (
          <div className="py-20 text-center">
            <Gift className="mx-auto mb-4 h-10 w-10 text-muted" aria-hidden="true" />
            <p className="text-muted">
              {i18n.language === 'fr'
                ? 'Aucun indicateur gratuit disponible pour le moment.'
                : 'No free indicators available at the moment.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {freeProducts.map((indicator) => {
              const translatedIndicator = translateProduct(indicator, i18n.language);
              return (
                <IndicatorCard
                  key={indicator._id}
                  indicator={{
                    ...translatedIndicator,
                    image:
                      translatedIndicator.image ||
                      translatedIndicator.images?.[0] ||
                      '/images/placeholder.png',
                  }}
                />
              );
            })}
          </div>
        )}

        {freeProducts.length > 0 && (
          <div className="mt-16 rounded-card border border-white/[.08] bg-surface px-6 py-10 text-center md:px-10">
            <h3 className="text-xl font-semibold tracking-tight text-body md:text-2xl">
              {i18n.language === 'fr'
                ? 'Prêt pour plus de fonctionnalités ?'
                : 'Ready for more features?'}
            </h3>
            <p className="mx-auto mt-3 max-w-lg text-sm text-muted md:text-base">
              {i18n.language === 'fr'
                ? "Découvrez notre gamme complète d'indicateurs premium avec des fonctionnalités avancées."
                : 'Discover our complete range of premium indicators with advanced features.'}
            </p>
            <div className="mt-6 flex justify-center">
              <Button to="/indicators">
                {i18n.language === 'fr' ? 'Voir tous les indicateurs' : 'View all indicators'}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeScreen;
